import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layout } from "../../components/layout/index.ts";
import { useAuth } from "../../lib/auth.tsx";
import { getTelegram } from "../../lib/telegram.ts";
import {
  IconUser,
  IconReceipt,
  IconChevronRight,
  IconShield,
  IconCog,
} from "../../components/Icons.tsx";

function displayName(account: {
  firstName: string | null;
  lastName: string | null;
  username: string | null;
}): string {
  const first = account.firstName?.trim();
  const last = account.lastName?.trim();
  if (first || last) {
    return [first, last].filter(Boolean).join(" ");
  }
  if (account.username) {
    return `@${account.username}`;
  }
  return "Пользователь";
}

export function ProfilePage() {
  const navigate = useNavigate();
  const tg = getTelegram();
  const { account, isMiniApp, logout } = useAuth();
  // Setup BackButton
  useEffect(() => {
    if (!tg) return;

    tg.BackButton.show();
    const handleBack = () => navigate(-1);
    tg.BackButton.onClick(handleBack);

    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  return (
    <Layout>
      <h1 className="page-title">Личный кабинет</h1>

      {/* Блок аккаунта — только информация, не кнопка (как в нативных настройках) */}
      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
        Аккаунт
      </p>
      <div
        role="presentation"
        className="rounded-xl bg-slate-100/90 py-3 px-4 flex items-center gap-3 mb-6 border-0 shadow-none [-webkit-tap-highlight-color:transparent]"
      >
        <div className="w-10 h-10 rounded-full bg-slate-200/80 flex items-center justify-center text-slate-500 shrink-0">
          <IconUser className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-[15px] text-slate-700 truncate">
            {account ? displayName(account) : "Гость"}
          </p>
          {account?.username &&
            (account.firstName?.trim() || account.lastName?.trim()) && (
              <p className="text-[13px] text-slate-500 mt-0.5 truncate">
                @{account.username}
              </p>
            )}
          {account?.email && (
            <p className="text-[13px] text-slate-500 mt-0.5 truncate">
              {account.email}
            </p>
          )}
        </div>
      </div>

      {/* Разделы — Заказы и билеты (одна страница, как в админке) */}
      <p className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide mb-3">Разделы</p>
      <div className="space-y-2">
        <Link
          to="/orders"
          className="flex items-center gap-3 p-4 rounded-2xl bg-white/95 shadow-sm active:bg-slate-50 transition-colors min-h-[56px] no-underline text-inherit"
        >
          <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600 shrink-0">
            <IconReceipt className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="font-medium text-[15px] text-slate-800 block truncate">Заказы и билеты</span>
            <span className="text-[13px] text-slate-500 block truncate">Платежи, билеты</span>
          </div>
          <IconChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
        </Link>
      </div>

      {/* Настройки и приложение — отдельная группа */}
      <p className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide mb-3 mt-6">Настройки</p>
      <div className="space-y-2">
        <Link
          to="/profile/settings"
          className="flex items-center gap-3 p-4 rounded-2xl bg-white/95 shadow-sm active:bg-slate-50 transition-colors min-h-[56px] no-underline text-inherit"
        >
          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 shrink-0">
            <IconCog className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="font-medium text-[15px] text-slate-800 block truncate">Настройки</span>
            <span className="text-[13px] text-slate-500 block truncate">Пароль, Telegram, email, приложение</span>
          </div>
          <IconChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
        </Link>
      </div>

      {/* Внизу: Админка, Выйти */}
      {account && (account.canAccessAdmin || !isMiniApp) && (
        <div className="mt-6 pt-4 border-t border-slate-200/60 space-y-2">
          {account.canAccessAdmin && (
            <Link
              to="/admin"
              className="flex items-center gap-3 p-4 rounded-2xl bg-white/95 shadow-sm active:bg-slate-50 transition-colors min-h-[56px] no-underline text-inherit w-full text-left"
            >
              <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600 shrink-0">
                <IconShield className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-medium text-[15px] text-slate-800 block truncate">Админка</span>
                <span className="text-[13px] text-slate-500 block truncate">Платежи, заказы, мероприятия</span>
              </div>
              <IconChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
            </Link>
          )}
          {!isMiniApp && (
            <button
              type="button"
              onClick={logout}
              className="w-full flex items-center gap-3 p-4 rounded-2xl bg-white/95 shadow-sm active:bg-slate-50 transition-colors min-h-[56px] text-left no-underline text-inherit"
            >
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center text-red-600 shrink-0">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </div>
              <span className="flex-1 min-w-0 font-medium text-[15px] text-red-600">Выйти из аккаунта</span>
              <IconChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
            </button>
          )}
        </div>
      )}

    </Layout>
  );
}
